import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ServiceDetailPageContainer } from './ServiceDetailPage';
import { FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ServiceImage = styled.img`
  max-width: 200px;
  height: auto;
  border-radius: 0;
  margin: 0.5rem 0 1rem 2rem;
  box-shadow: none;
  float: right;

  @media (max-width: 768px) {
    float: none;
    margin: 1rem auto;
    max-width: 80%;
    display: block;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  color: #fff !important;
  margin-top: 0 !important;
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function ServerServices() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to exactly 11 digits
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDetailPageContainer>
      <h1>Sunucu Hizmetleri</h1>
      <p>İşletmenizin dijital altyapısını güçlendirecek, yüksek performanslı ve güvenli sunucu çözümleri sunuyoruz. Modern teknoloji ve uzman ekibimizle, işletmenizin ihtiyaçlarına özel sunucu hizmetleri sağlıyoruz.</p>
      
      <ServiceImage src="/media/demirer-ajans-1.png" alt="Sunucu Hizmetleri Görseli 1" />

      <h2>Neden Profesyonel Sunucu Hizmetleri Önemlidir?</h2>
      <p>Dijital dünyada başarılı olmanın temelinde güvenilir ve performanslı bir sunucu altyapısı yatar. Profesyonel sunucu hizmetleri, web sitenizin hızını, güvenliğini ve erişilebilirliğini doğrudan etkiler. Bu da kullanıcı deneyimini ve işletmenizin online performansını belirler.</p>

      <ServiceImage src="/media/demirer-ajans-2.png" alt="Sunucu Hizmetleri Görseli 2" />

      <h3>Sunucu Hizmetlerimiz Neleri Kapsar?</h3>
      <ul>
        <li><b>Yüksek Performanslı Sunucular:</b> En son teknoloji sunucular ile kesintisiz hizmet ve maksimum performans.</li>
        <li><b>Güvenlik Çözümleri:</b> Gelişmiş güvenlik protokolleri, DDoS koruması ve güvenlik duvarı yapılandırmaları.</li>
        <li><b>Ölçeklenebilir Altyapı:</b> İşletmenizin büyümesine uyum sağlayan, esnek sunucu çözümleri.</li>
        <li><b>7/24 Teknik Destek:</b> Uzman ekibimiz ile kesintisiz teknik destek ve bakım hizmeti.</li>
        <li><b>Yedekleme ve Kurtarma:</b> Düzenli yedekleme ve hızlı kurtarma çözümleri ile veri güvenliği.</li>
        <li><b>SSL Sertifikaları:</b> Güvenli veri transferi için SSL sertifika yönetimi ve kurulumu.</li>
        <li><b>Sunucu Optimizasyonu:</b> Performans artırıcı sunucu optimizasyonları ve yapılandırmaları.</li>
        <li><b>İzleme ve Raporlama:</b> Sunucu performansını sürekli izleme ve detaylı raporlama hizmetleri.</li>
      </ul>

      <ServiceImage src="/media/demirer-ajans-3.png" alt="Sunucu Hizmetleri Görseli 3" />

      <h2>Sunucu Hizmetlerimizle İşletmenizi Güçlendirin</h2>
      <p>Her işletmenin ihtiyaçları farklıdır. Bu nedenle, sunucu çözümlerimizi işletmenizin özel gereksinimlerine göre şekillendiriyoruz. Teknik ekibimiz, işletmenizin ihtiyaçlarını analiz ederek en uygun sunucu çözümünü belirler ve uygular.</p>
      <p>Güvenilir, hızlı ve ölçeklenebilir sunucu hizmetlerimizle, işletmenizin dijital varlığını güçlendiriyor, online performansınızı en üst seviyeye çıkarıyoruz. Dijital dünyada rekabet avantajı elde etmek için bize güvenebilirsiniz.</p>

      <h2>Sunucu Hizmetlerimiz</h2>
      <p>Sunucu hizmetlerimiz, işletmenizin dijital altyapısını güçlendirmek için tasarlanmıştır. Uzman ekibimiz, sunucu kurulumu, yönetimi ve bakımı konularında profesyonel çözümler sunar.</p>
      <p>Modern teknolojiler ve güvenlik önlemleriyle, işletmenizin verilerini güvende tutuyor ve kesintisiz hizmet sağlıyoruz. İşletmenizin dijital varlığını güçlendirmek için bize güvenebilirsiniz.</p>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={formData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={formData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServiceDetailPageContainer>
  );
}

export default ServerServices; 